<script setup lang="ts">
// COMPONENTS
import { GSkeleton, GSkeletonItem } from '@flash-global66/g-skeleton';

// TYPES
import type { LayoutContentSkeletonProps } from '@/shared/layouts/types/mainLayout.type';

/**
 * Placeholder de carga del contenido de MainLayout. `home` y `onboarding` copian la
 * geometría (paddings, gaps y alturas) del estado de carga real de cada pantalla, para
 * que al montarse solo aparezcan los datos sin que nada cambie de lugar.
 */
defineOptions({ name: 'LayoutContentSkeleton' });

withDefaults(defineProps<LayoutContentSkeletonProps>(), {
  variant: 'generic',
  isLiveRegion: true,
});
</script>

<template>
  <div
    :role="isLiveRegion ? 'status' : undefined"
    :aria-busy="isLiveRegion ? 'true' : undefined"
    :aria-label="isLiveRegion ? 'Cargando' : undefined"
    data-test="layout:content-skeleton"
    class="flex-1 overflow-hidden"
  >
    <GSkeleton animated>
      <template #template>
        <div
          v-if="variant === 'home'"
          data-test="layout:content-skeleton-home"
          class="px-4 md:px-8 py-7 grid grid-cols-1 md:grid-cols-12 gap-6"
        >
          <div class="col-span-12 flex flex-col gap-[18px]">
            <div class="h-[30px] flex items-center">
              <GSkeletonItem variant="text" class="h-5 w-36" />
            </div>

            <div>
              <div class="h-[22px] flex items-center justify-end">
                <GSkeletonItem variant="text" class="h-4 w-32" />
              </div>
              <div class="h-md" />
              <div class="flex gap-4 md:gap-8">
                <GSkeletonItem
                  v-for="account in 2"
                  :key="account"
                  variant="rect"
                  class="flex-1 rounded-lg h-[132px]"
                />
              </div>
            </div>

            <div class="flex flex-col gap-4">
              <div class="h-[30px] flex items-center">
                <GSkeletonItem variant="text" class="h-5 w-44" />
              </div>
              <div class="flex flex-col gap-4 md:grid md:grid-cols-4">
                <div
                  v-for="action in 4"
                  :key="action"
                  class="shadow-sm bg-white border border-grey-50 rounded-lg p-md flex items-center gap-x-2"
                >
                  <GSkeletonItem variant="circle" class="h-10 w-10 shrink-0" />
                  <GSkeletonItem variant="text" class="h-5 w-24" />
                </div>
              </div>
            </div>
          </div>

          <div class="col-span-12 xl:col-span-8 flex flex-col gap-2.5">
            <div class="h-7 flex items-center gap-sm">
              <GSkeletonItem variant="text" class="h-5 w-52" />
              <GSkeletonItem variant="rect" class="h-7 w-44 rounded-full" />
            </div>
            <div class="h-[266px] flex flex-col gap-md rounded-lg bg-white px-md py-lg shadow-sm">
              <GSkeletonItem variant="text" class="h-5 w-full" />
              <GSkeletonItem variant="text" class="h-5 w-full" />
              <GSkeletonItem variant="text" class="h-5 w-2/3" />
            </div>
          </div>

          <div class="col-span-12 xl:col-span-4 flex flex-col gap-2.5">
            <div class="h-7 flex items-center">
              <GSkeletonItem variant="text" class="h-5 w-36" />
            </div>
            <div class="h-[266px] flex flex-col gap-4 rounded-lg bg-white px-md py-lg shadow-sm">
              <div class="flex gap-1">
                <GSkeletonItem
                  v-for="input in 2"
                  :key="input"
                  variant="rect"
                  class="flex-1 h-12 rounded-lg"
                />
              </div>
              <div class="flex gap-[10px] justify-center">
                <GSkeletonItem
                  v-for="period in 6"
                  :key="period"
                  variant="rect"
                  class="h-4 w-8 rounded"
                />
              </div>
            </div>
          </div>

          <GSkeletonItem variant="rect" class="col-span-12 h-[132px] rounded-lg" />
        </div>

        <div
          v-else-if="variant === 'onboarding'"
          data-test="layout:content-skeleton-onboarding"
          class="px-4 md:px-12 py-5"
        >
          <section
            class="mt-6 grid grid-cols-1 md:grid-cols-2 gap-xl md:gap-2xl bg-white rounded-lg shadow-md px-lg py-xl md:px-2xl md:py-xl"
          >
            <div class="flex flex-col justify-center items-start gap-lg">
              <GSkeletonItem variant="rect" class="h-[22px] w-24 rounded-full" />
              <div class="h-9 flex items-center w-full">
                <GSkeletonItem variant="text" class="h-6 w-3/5" />
              </div>
              <div class="flex items-center gap-md w-full">
                <GSkeletonItem variant="rect" class="h-[44px] w-[44px] shrink-0 rounded-md" />
                <div class="h-12 flex-1 flex flex-col justify-around">
                  <GSkeletonItem variant="text" class="h-4 w-full" />
                  <GSkeletonItem variant="text" class="h-4 w-2/3" />
                </div>
              </div>
              <GSkeletonItem variant="rect" class="h-3xl w-56 rounded-md" />
            </div>

            <div class="flex flex-col justify-center gap-lg">
              <div class="h-7 flex items-center">
                <GSkeletonItem variant="text" class="h-5 w-3/5" />
              </div>
              <div>
                <GSkeletonItem variant="rect" class="h-1 w-full rounded-full" />
                <div class="h-[18px] mt-xs flex items-center justify-between">
                  <GSkeletonItem variant="text" class="h-3 w-12" />
                  <GSkeletonItem variant="text" class="h-3 w-12" />
                </div>
              </div>
              <div class="flex flex-col gap-md">
                <div
                  v-for="step in 7"
                  :key="step"
                  class="h-[22px] flex items-center gap-sm"
                >
                  <GSkeletonItem variant="circle" class="h-5 w-5 shrink-0" />
                  <GSkeletonItem variant="text" class="h-4 w-3/4" />
                </div>
              </div>
            </div>
          </section>

          <!-- El alto fijo de GSkeletonItem anula aspect-ratio: la proporción va en el contenedor -->
          <div class="mt-6 w-full aspect-[4160/1464]">
            <GSkeletonItem variant="rect" class="block h-full w-full rounded-lg" />
          </div>
        </div>

        <div
          v-else
          data-test="layout:content-skeleton-generic"
          class="px-4 md:px-8 py-7"
        >
          <GSkeletonItem variant="h3" class="h-7 w-56 mb-6" />
          <GSkeletonItem variant="rect" class="h-96 w-full rounded-xl" />
        </div>
      </template>
    </GSkeleton>
  </div>
</template>
